import React from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { PaidSearchCampaignsGrid } from './PaidSearchCampaignsGrid';
import { PaidSearchKeywordGrid } from './PaidSearchKeywordGrid';
import { PaidSearchProductsGrid } from './PaidSearchProductsGrid';
import { PaidSearchCampaignTypeGrid } from './PaidSearchCampaignTypeGrid';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useCompanyName, useMainEntityType, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { isTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { GridButtonGroupBase } from 'src/components/common/Buttons/GridButtonGroupBase';
import AppExportModal from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import { useTopicExportModalStore } from 'src/zustandStores/BeaconPro/Topics/Exports/TopicExportModalStore';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const options = [
  {
    id: 'keyword',
    label: 'Keyword'
  },
  {
    id: 'product',
    label: 'Product'
  },
  {
    id: 'campaign',
    label: 'Campaign'
  },
  {
    id: 'campaignType',
    label: 'Campaign Type'
  }
];

/**
 * Dropdown grid for paid search topics
 */
export function PaidSearchTopicsGrid() {
  const { isProductEntity } = useProductEntity();
  const [selectedDropdown, setSelectedDropdown] = React.useState('keyword');
  const mainEntityType = useMainEntityType();
  const isCategoryEntity = mainEntityType === 'category';
  const isSubcategoryEntity = mainEntityType === 'subcategory';
  const isTopic = isTopicOrTopicGroupType(mainEntityType);
  const titleOnly = !(isProductEntity || isCategoryEntity || isSubcategoryEntity || isTopic);
  const entityPhrase = useEntityPhrasing({ titleOnly });
  const taxonomyText = useTaxonomySubtitleText();
  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const companyName = useCompanyName();
  const [isExportModalOpen, setIsExportModalOpen] = React.useState(false);
  const { openModal: openTopicExport } = useTopicExportModalStore();
  /**
   *The table displays the [advertising metrics] for [Kelloggs] from [all 5 account categories] by [keyword] during the [last 52 week] period compared to the [prior year].
   */
  const getSubtitle = () => {
    const displayName = options.find((option) => option.id === selectedDropdown)?.label ?? '';
    return (
      <Flex>
        <Text variant="body1">
          {`The table displays the advertising metrics ${!isTopic ? `for ${entityPhrase}` : `for ${companyName}`}`}
          {taxonomyText}
          {isTopic ? ` in ${entityPhrase}` : ''}
          {` by ${displayName.toLowerCase()} during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </Flex>
    );
  };

  return (
    <Flex flexDirection="column" gap="lg">
      <Flex flexDirection="column" gap="md">
        <Flex justifyContent="space-between">
          <Flex alignItems="center" gap={6}>
            <Text variant="h4">Ad Metrics by</Text>
            <SlDropdownMenu
              buttonVariant="title"
              options={options}
              selectedId={selectedDropdown}
              onChange={({ id }) => setSelectedDropdown(id)}
            />
          </Flex>

          <GridButtonGroupBase
            icons={{
              export: true
            }}
            onExport={() => {
              // We have 2 different export modals, one for topic-related exports and one for all others
              if (!isTopic) {
                setIsExportModalOpen(true);
              } else {
                openTopicExport();
              }
            }}
          />
        </Flex>
        <Text>{getSubtitle()}</Text>
      </Flex>
      {selectedDropdown === 'campaign' && <PaidSearchCampaignsGrid />}
      {selectedDropdown === 'keyword' && <PaidSearchKeywordGrid />}
      {selectedDropdown === 'product' && <PaidSearchProductsGrid />}
      {selectedDropdown === 'campaignType' && <PaidSearchCampaignTypeGrid />}
      {isExportModalOpen && <AppExportModal open={isExportModalOpen} handleClose={() => setIsExportModalOpen(false)} />}
    </Flex>
  );
}
